// Smart dark color generation utility

/**
 * Converts HSL string to HSL values
 * @param hslString - e.g., "hsl(20, 84%, 45%)"
 */
export function parseHsl(hslString: string): {
  h: number;
  s: number;
  l: number;
} {
  const match = hslString.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
  if (!match) {
    throw new Error(`Invalid HSL string: ${hslString}`);
  }
  return {
    h: parseInt(match[1]),
    s: parseInt(match[2]),
    l: parseInt(match[3]),
  };
}

/**
 * Converts HSL values back to HSL string
 */
export function hslToString(h: number, s: number, l: number): string {
  return `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`;
}

/**
 * Calculates the relative luminance of a color for contrast ratio calculation
 * Based on WCAG guidelines
 */
function getRelativeLuminance(h: number, s: number, l: number): number {
  // Convert HSL to RGB first
  const c = ((1 - Math.abs((2 * l) / 100 - 1)) * s) / 100;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l / 100 - c / 2;

  let r = 0,
    g = 0,
    b = 0;
  if (h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  r = r + m;
  g = g + m;
  b = b + m;

  // Apply gamma correction and calculate luminance
  const toLinear = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Calculates contrast ratio between two colors
 */
function getContrastRatio(l1: number, l2: number): number {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Generates a smart dark mode variant of a color
 * @param lightColor - HSL string of the light mode color
 * @param type - Type of color to adjust generation strategy
 */
export function generateDarkColor(
  lightColor: string,
  type: "primary" | "secondary" | "primaryForeground" | "secondaryForeground",
): string {
  try {
    const { h, s, l } = parseHsl(lightColor);

    if (type.includes("Foreground")) {
      // For text colors in dark mode, prioritize readability over color matching
      // Default to high contrast white/near-white for button text
      return "hsl(0, 0%, 98%)"; // Almost pure white for maximum contrast
    } else {
      // For primary/secondary colors, make them work well on dark backgrounds
      // while maintaining good contrast with white text
      if (l > 60) {
        // Very light colors: darken significantly but keep vibrant
        return hslToString(h, Math.min(s + 10, 100), Math.max(l - 30, 35));
      } else if (l > 40) {
        // Medium colors: darken moderately
        return hslToString(h, Math.min(s + 5, 100), Math.max(l - 15, 25));
      } else {
        // Dark colors: lighten to work on dark background
        return hslToString(h, Math.min(s + 15, 100), Math.min(l + 25, 70));
      }
    }
  } catch (error) {
    console.warn(`Failed to generate dark color for ${lightColor}:`, error);
    // Fallback: white text for foreground, original color for others
    if (type.includes("Foreground")) {
      return "hsl(0, 0%, 98%)";
    }
    return lightColor;
  }
}

/**
 * Generates a complete set of dark colors based on light colors
 */
export function generateDarkColorSet(lightColors: {
  primary?: string;
  primaryForeground?: string;
  secondary?: string;
  secondaryForeground?: string;
}) {
  return {
    primary: lightColors.primary
      ? generateDarkColor(lightColors.primary, "primary")
      : undefined,
    primaryForeground: lightColors.primaryForeground
      ? generateDarkColor(lightColors.primaryForeground, "primaryForeground")
      : undefined,
    secondary: lightColors.secondary
      ? generateDarkColor(lightColors.secondary, "secondary")
      : undefined,
    secondaryForeground: lightColors.secondaryForeground
      ? generateDarkColor(
          lightColors.secondaryForeground,
          "secondaryForeground",
        )
      : undefined,
  };
}
